<template>
  <CModal
    :show.sync="visible"
    size="lg"
    @hidden="onHidden"
    :close-on-backdrop="false"
  >
    <template #header>
      <h5 class="modal-title">
        <translate translate-context="event_owner.delete">Delete Event Owner</translate>
      </h5>
      <CButtonClose @click="close" />
    </template>
    <div class="modal-body">
      <div v-if="loading" class="text-center py-4">
        <CSpinner color="primary" />
      </div>

      <div v-else-if="dependencies">
        <CAlert v-if="dependencies.can_delete === false" color="danger">
          <CIcon name="cipWarning" size="lg" class="mr-2" />
          <translate translate-context="event_owner.delete">
            This Event Owner cannot be deleted because it has dependencies that must be handled manually first.
          </translate>
        </CAlert>

        <CAlert v-else-if="!dependencies.requires_force" color="info">
          <translate translate-context="event_owner.delete">
            This Event Owner has no dependencies and can be safely deleted.
          </translate>
        </CAlert>

        <CAlert v-else color="warning">
          <translate translate-context="event_owner.delete">
            This Event Owner has dependencies. Review them carefully before proceeding.
          </translate>
        </CAlert>

        <div v-if="dependencies.message" class="mb-3">
          <p>{{ dependencies.message }}</p>
        </div>
      </div>
    </div>
    <template #footer>
      <CButton color="secondary" @click="close">
        <translate translate-context="event_owner.delete">Cancel</translate>
      </CButton>
      <CButton
        v-if="dependencies && !loading && dependencies.can_delete !== false"
        color="danger"
        @click="onConfirmDelete"
        :disabled="deleting"
      >
        <CSpinner v-if="deleting" size="sm" class="mr-1" />
        <translate translate-context="event_owner.delete">Proceed with Deletion</translate>
      </CButton>
      <CButton
        v-else-if="dependencies && !loading"
        color="danger"
        disabled
      >
        <CIcon name="cipBan" size="sm" class="mr-1" />
        <translate translate-context="event_owner.delete">Cannot Delete</translate>
      </CButton>
    </template>
  </CModal>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'

export default {
  name: 'EventOwnerDeleteModal',

  data() {
    return {
      visible: false,
      eventOwnerId: null,
      eventOwnerName: null,
      loading: false,
      deleting: false,
    }
  },

  computed: {
    ...mapGetters('eventOwner', ['deleteDependencies']),
    dependencies() {
      return this.deleteDependencies
    }
  },

  methods: {
    ...mapActions('eventOwner', [
      'checkEventOwnerDependencies',
      'deleteEventOwner'
    ]),

    async open(eventOwner) {
      this.eventOwnerId = eventOwner.id
      this.eventOwnerName = eventOwner.company_name
      this.loading = true
      this.visible = true

      try {
        await this.checkEventOwnerDependencies(eventOwner.id)
      } catch (error) {
        this.notifyError(this.$pgettext('event_owner.delete', 'Failed to check dependencies'))
        this.close()
      } finally {
        this.loading = false
      }
    },

    close() {
      this.visible = false
    },

    onHidden() {
      this.eventOwnerId = null
      this.eventOwnerName = null
      this.loading = false
      this.deleting = false
    },

    async onConfirmDelete() {
      if (this.dependencies.can_delete === false) {
        this.notifyError(this.$pgettext('event_owner.delete', 'Cannot delete: dependencies must be handled manually first'))
        return
      }

      this.deleting = true
      try {
        await this.deleteEventOwner({
          id: this.eventOwnerId,
          force: this.dependencies.requires_force || false
        })
        await this.notifySuccess(this.$pgettext('event_owner.delete', 'Event Owner deleted successfully'))
        this.close()
        this.$emit('deleted')
      } catch (error) {
        if (error && error.response && error.response.data && error.response.data.error) {
          await this.notifyError(this.$pgettext('event_owner.delete', 'Failed to delete Event Owner: ' + error.response.data.error))
        } else {
          await this.notifyError(this.$pgettext('event_owner.delete', 'Failed to delete Event Owner'))
        }
      } finally {
        this.deleting = false
      }
    },
  }
}
</script>
